import React, { useEffect, useRef, useState } from 'react';
import type { FC } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { Flex } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useExpandSideNav } from '@atlaskit/navigation-system/layout/side-nav';

import { OnboardingNudge } from '@confluence/onboarding-nudge-tooltip/entry-points/OnboardingNudgeTooltip';
import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState';
import { HOME } from '@confluence/named-routes/entry-points/routes';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets';
import { useTransitionId } from '@confluence/route-manager/entry-points/RouteState';
import { HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { fg } from '@confluence/feature-gating';

import { useIsEligibleForNthUserHomeOnboardingTakeoverV2HomeNudge } from '../hooks/useIsEligibleForNthUserHomeOnboardingTakeoverV2HomeNudge';
import {
	PopoverProvider,
	PopoverTarget,
	PopoverContent,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightBody,
	SpotlightFooter,
	SpotlightActions,
	SpotlightPrimaryAction,
	SpotlightSecondaryAction,
	SpotlightCard,
} from '@atlaskit/spotlight';

const i18n = defineMessages({
	homeSpotlightHeader: {
		id: 'experiment-home-onboarding-takeover-v2.nth-user-home-nudge.header',
		defaultMessage: 'Get started with Confluence',
		description: 'Message that gets displayed in Spotlight header',
	},
	homeSpotlightText: {
		id: 'experiment-home-onboarding-takeover-v2.nth-user-home-nudge.text',
		defaultMessage: 'Quickly learn about Confluence and become a Confluence expert',
		description: 'Message that gets displayed in spotlight message',
	},
	homeSpotlightCloseButtonText: {
		id: 'experiment-home-onboarding-takeover-v2.nth-user-home-nudge.button-dismiss',
		defaultMessage: 'Dismiss',
		description: 'Close button that gets displayed in spotlight message',
	},
	homeSpotlightTryItButtonText: {
		id: 'experiment-home-onboarding-takeover-v2.nth-user-home-nudge.button-try-it',
		defaultMessage: 'Try it',
		description: 'Try it button that gets displayed in spotlight message',
	},
	homeSpotlightHeaderV2: {
		id: 'experiment-home-onboarding-takeover-v2.nth-user-home-nudge.title',
		defaultMessage: 'More ways to get started 💡',
		description: 'Message that gets displayed in spotlight header section',
	},
	homeSpotlightTextV2: {
		id: 'experiment-home-onboarding-takeover-v2.nth-user-home-nudge.description',
		defaultMessage: 'When you’re ready, discover tailored onboarding suggestions in For You.',
		description: 'Message that gets displayed in spotlight message',
	},
	homeSpotlightCloseButtonTextV2: {
		id: 'experiment-home-onboarding-takeover-v2.nth-user-home-nudge.button-got-it',
		defaultMessage: 'Got it',
		description: 'button to dismiss spotlight that gets displayed in spotlight message',
	},
	homeSpotlightTryItButtonTextV2: {
		id: 'experiment-home-onboarding-takeover-v2.nth-user-home-nudge.button-go-to-for-you',
		defaultMessage: 'Go to For You',
		description: 'button to navigate to For You that gets displayed in spotlight message',
	},
});

const NudgeFixer: FC = ({ children }: { children?: React.ReactNode }) => {
	return (
		<Flex direction="column" justifyContent="center">
			{children}
		</Flex>
	);
};

type NthUserHomeOnboardingTakeoverV2HomeNudgeProps = {
	position?: 'bottom-start' | 'right-start';
	children?: React.ReactNode;
};

export const NthUserHomeOnboardingTakeoverV2HomeNudge: FC<
	NthUserHomeOnboardingTakeoverV2HomeNudgeProps
> = ({ children, position = 'bottom-start' }) => {
	const [isHomeNudgeHidden, setIsHomeNudgeHidden] = useState(true);
	const wasEligibleForOnboardingNudgeRef = useRef(false);
	const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

	const { formatMessage } = useIntl();
	const { setOnboardingState } = useSetOnboardingState();

	const { push } = useRouteActions();
	// This is the onboarding state hidden
	const { isEligibleForHomeNudge, isOnHome, error, isHomeNudgeScheduledAt } =
		useIsEligibleForNthUserHomeOnboardingTakeoverV2HomeNudge();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { useChoreographer } = useChoreographerAssets();
	const expandSideNav = useExpandSideNav();
	const transitionId = useTransitionId();
	const transitionIdRef = useRef<number | null>(null);

	const { startMessage, stopMessage } = useChoreographer(
		'nth-user-home-onboarding-takeover-v2-home-nudge',
		{
			start: () => setIsHomeNudgeHidden(false),
			stop: () => setIsHomeNudgeHidden(true),
		},
	);

	const handleDismiss = () => {
		setIsHomeNudgeHidden(true);
		void stopMessage();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'nthUserHomeOnboardingTakeoverV2HomeNudgeDismiss',
				source: 'onboarding',
			},
		}).fire();
	};

	const handleGoHome = () => {
		push(HOME.toUrl());
		void stopMessage();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'nthUserHomeOnboardingTakeoverV2HomeNudgeGoHome',
				source: 'onboarding',
			},
		}).fire();
	};

	useEffect(() => {
		const showNudge = () => {
			void setOnboardingState({
				key: HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS.hasSeenHomeNudge,
				value: 'true',
			});
			void startMessage();
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					action: 'viewed',
					name: 'nthUserHomeOnboardingTakeoverV2HomeNudge',
				},
			}).fire();
			expandSideNav();
		};

		if (fg('cc_nth_user_onboarding_nudge_timing_fix')) {
			if (
				isEligibleForHomeNudge &&
				isHomeNudgeHidden &&
				(!wasEligibleForOnboardingNudgeRef.current || transitionIdRef.current !== transitionId)
			) {
				// first time about to show nudge, wait 2mins seconds before showing
				// Upon subsequent reloads or navigation, show the nudge immediately
				if (!timeoutRef.current && !isHomeNudgeScheduledAt) {
					timeoutRef.current = setTimeout(
						() => {
							showNudge();
							clearTimeout(timeoutRef.current);
						},
						2 * 1000 * 60,
					);
					void setOnboardingState({
						key: HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS.isHomeNudgeScheduledAt,
						value: new Date().toISOString(),
					});
				} else if (
					isHomeNudgeScheduledAt &&
					(transitionId === 0 || transitionIdRef.current !== transitionId)
				) {
					showNudge();
					clearTimeout(timeoutRef.current);
				}
				wasEligibleForOnboardingNudgeRef.current = true;
			}
			transitionIdRef.current = transitionId;
		} else {
			if (
				isEligibleForHomeNudge &&
				isHomeNudgeHidden &&
				!wasEligibleForOnboardingNudgeRef.current
			) {
				showNudge();
				wasEligibleForOnboardingNudgeRef.current = true;
			}
		}
	}, [
		createAnalyticsEvent,
		isEligibleForHomeNudge,
		isHomeNudgeHidden,
		setOnboardingState,
		startMessage,
		stopMessage,
		expandSideNav,
		transitionId,
		isHomeNudgeScheduledAt,
	]);

	useEffect(() => {
		return () => {
			if (fg('cc_nth_user_onboarding_nudge_timing_fix')) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const isNudgeHidden =
		!isHomeNudgeHidden && (wasEligibleForOnboardingNudgeRef.current || isEligibleForHomeNudge);

	return fg('nth_user_onboarding_copy_and_spotlight_updates') ? (
		<PopoverProvider>
			<PopoverTarget>{children}</PopoverTarget>
			<PopoverContent
				dismiss={handleDismiss}
				placement="right-end"
				isVisible={isNudgeHidden && !isOnHome}
			>
				<SpotlightCard testId="spotlight">
					<SpotlightHeader>
						<SpotlightHeadline>{formatMessage(i18n.homeSpotlightHeaderV2)}</SpotlightHeadline>
						<SpotlightControls>
							<SpotlightDismissControl onClick={handleDismiss} />
						</SpotlightControls>
					</SpotlightHeader>
					<SpotlightBody>{formatMessage(i18n.homeSpotlightTextV2)}</SpotlightBody>
					<SpotlightFooter>
						<SpotlightActions>
							<SpotlightPrimaryAction onClick={handleGoHome}>
								{formatMessage(i18n.homeSpotlightTryItButtonTextV2)}
							</SpotlightPrimaryAction>
							<SpotlightSecondaryAction onClick={handleDismiss}>
								{formatMessage(i18n.homeSpotlightCloseButtonTextV2)}
							</SpotlightSecondaryAction>
						</SpotlightActions>
					</SpotlightFooter>
				</SpotlightCard>
			</PopoverContent>
		</PopoverProvider>
	) : (
		<OnboardingNudge
			hidden={!isNudgeHidden || isOnHome}
			setHidden={() => stopMessage()}
			position={position}
			wrapper={NudgeFixer}
			heading={formatMessage(i18n.homeSpotlightHeader)}
			actions={[
				{
					onClick: handleGoHome,
					text: formatMessage(i18n.homeSpotlightTryItButtonText),
				},
				{
					onClick: handleDismiss,
					text: formatMessage(i18n.homeSpotlightCloseButtonText),
				},
			]}
			actionSubjectId="nth-user-home-onboarding-takeover-v2-home-nav-item"
			key="nth-user-home-onboarding-takeover-v2-home-nav-item"
			width={275}
			source="onboarding"
			content={formatMessage(i18n.homeSpotlightText)}
			renderChildrenWhileLoading
		>
			{children}
			{error && <ErrorDisplay error={error} />}
		</OnboardingNudge>
	);
};
